import { components } from "@/api-schema";
import { RelocationOrderBranchRejectStatus, VehicleBlockReason } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { RelocationOrderFlowAction } from "@/common/api/relocation-order-flow/_helpers/resolve-relocation-order-flow-path";
import { useRelocationOrderFlowCancelMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow-cancel.mutation";
import { useRelocationOrderFlowMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow.mutation";
import { useVehicleBlockCreateMutation } from "@/common/api/vehicle-block/use-vehicle-block-create.mutation";
import { useVehicleBlockDeleteMutation } from "@/common/api/vehicle-block/use-vehicle-block-delete.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { AttributeAppRolePrefix, extractAttributeAppRolesByPrefix } from "@/common/helpers/user-attribute-app-roles";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DataTableRow } from "@/components/data-table/types";
import { RowActionVariant, type RowAction } from "@/components/ui/table-row-actions";
import { VehicleBlockDialog } from "@/components/vehicle-block/vehicle-block-dialog";
import { createVehicleBlockPayload } from "@/components/vehicle-block/_helpers/vehicle-block-payload";
import { userStore } from "@/store/user-store";
import { BanIcon, EditIcon, EyeIcon } from "lucide-react";
import { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-browser-location";
import { RejectVehicleDialog } from "@/components/relocation-order/reject-vehicle-dialog";
import { ResolveIssueDialog } from "@/components/relocation-order/resolve-issue-dialog";
import { canCancelRelocationOrder } from "./_helpers/can-cancel-relocation-order";
import type { RelocationOrderActionsContext } from "./_helpers/relocation-order-actions-context";
import { buildActionsForOrder } from "./_helpers/relocation-order-actions-dispatcher";
import { sortRelocationOrderActions } from "./_helpers/sort-relocation-order-actions";

type RelocationOrder = components["schemas"]["RelocationOrder"];
type RelocationOrderRow = components["schemas"]["RelocationOrder"] & RowWithIdentifier;
type Vehicle = components["schemas"]["Vehicle"];
type Branch = components["schemas"]["Branch"];

enum ResolveMode {
  TransportCompany = "transport-company",
  BranchRejected = "branch-rejected",
}

interface UseRelocationOrderInternalActionsReturn {
  userBranchesUuids: string[];
  isLoadingUserBranches: boolean;
  canManage: boolean;
  customRowActions: (row: DataTableRow<RelocationOrderRow>) => RowAction[];
  getActionsForOrder: (relocationOrder: RelocationOrder) => RowAction[];
  dialogs: JSX.Element;
}

export function useRelocationOrderInternalActions(options?: {
  enabled?: boolean;
}): UseRelocationOrderInternalActionsReturn {
  const { t } = useTranslation(["fit", "common"]);
  const { user } = userStore();
  const ability = useAbility();
  const canManage = ability.can("manage", "RelocationOrder");

  const userBranchAttributeAppRoles = extractAttributeAppRolesByPrefix(user?.roles, AttributeAppRolePrefix.Branch);
  const { data: userBranches, isLoading: isLoadingUserBranches } = useBranchSearchQuery({
    options: {
      body: {
        technicalId: userBranchAttributeAppRoles,
      },
      parameters: {
        pageSize: UNLIMITED_PAGE_SIZE,
      },
    },
    enabled: (options?.enabled ?? true) && userBranchAttributeAppRoles.length > 0,
  });
  const userBranchesUuids = useMemo(() => {
    return userBranches?.data?.map((branch: Branch) => branch.uuid) ?? [];
  }, [userBranches]);

  const { mutateAsync: branchApproveReadyForTransportMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchApproveReadyForTransport,
  );
  const { mutateAsync: branchAcceptVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchAcceptVehicle,
  );
  const { mutateAsync: branchCompleteMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchComplete,
  );
  const { mutateAsync: branchRejectVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchRejectVehicle,
  );
  const { mutateAsync: branchResolveTransportCompanyRejectedVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchResolveTransportCompanyRejectedVehicle,
  );
  const { mutateAsync: branchResolveBranchRejectedVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.BranchResolveBranchRejectedVehicle,
  );
  const { mutateAsync: cancelRelocationOrderMutation, isPending: isCancellingRelocationOrder } =
    useRelocationOrderFlowCancelMutation();
  const { mutateAsync: deleteVehicleBlockMutation } = useVehicleBlockDeleteMutation();
  const { mutateAsync: createVehicleBlockMutation, isPending: isBlockingVehicle } = useVehicleBlockCreateMutation();

  const [rejectDialogIsOpen, setRejectDialogIsOpen] = useState(false);
  const [resolveDialogIsOpen, setResolveDialogIsOpen] = useState(false);
  const [resolveMode, setResolveMode] = useState<ResolveMode | null>(null);
  const [selectedRelocationOrder, setSelectedRelocationOrder] = useState<RelocationOrder | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [blockReason, setBlockReason] = useState<VehicleBlockReason>(VehicleBlockReason.Maintenance);
  const [blockDuration, setBlockDuration] = useState<number>(1);

  const handleApproveReadyForTransport = useCallback(
    async (relocationOrder: RelocationOrder) => {
      await toastMutationStatus({
        mutationFn: () => branchApproveReadyForTransportMutation(relocationOrder),
        successMessage: t("fit:TransportSuccessfullyApproved"),
        errorMessage: t("fit:ErrorApprovingTransport"),
      });
    },
    [branchApproveReadyForTransportMutation, t],
  );

  const handleResolveTransportCompanyIssue = useCallback((relocationOrder: RelocationOrder) => {
    setSelectedRelocationOrder(relocationOrder);
    setResolveMode(ResolveMode.TransportCompany);
    setResolveDialogIsOpen(true);
  }, []);

  const handleResolveBranchRejectedIssue = useCallback((relocationOrder: RelocationOrder) => {
    setSelectedRelocationOrder(relocationOrder);
    setResolveMode(ResolveMode.BranchRejected);
    setResolveDialogIsOpen(true);
  }, []);

  const handleConfirmResolveIssue = useCallback(
    async (resolveNote: string) => {
      if (!selectedRelocationOrder) return;

      if (resolveMode === ResolveMode.TransportCompany) {
        await toastMutationStatus({
          mutationFn: () =>
            branchResolveTransportCompanyRejectedVehicleMutation({
              relocationOrder: selectedRelocationOrder,
              body: { resolveNote },
            }),
          successMessage: t("fit:NoteHasBeenSuccessfullyUpdated"),
          errorMessage: t("fit:ErrorUpdatingTheNote"),
          onSuccess: () => {
            setResolveDialogIsOpen(false);
          },
        });
      } else if (resolveMode === ResolveMode.BranchRejected) {
        await toastMutationStatus({
          mutationFn: () =>
            branchResolveBranchRejectedVehicleMutation({
              relocationOrder: selectedRelocationOrder,
              body: { resolveNote },
            }),
          successMessage: t("fit:NoteHasBeenSuccessfullyUpdated"),
          errorMessage: t("fit:ErrorUpdatingTheNote"),
          onSuccess: () => {
            setResolveDialogIsOpen(false);
          },
        });
      }
    },
    [
      selectedRelocationOrder,
      resolveMode,
      branchResolveTransportCompanyRejectedVehicleMutation,
      branchResolveBranchRejectedVehicleMutation,
      t,
    ],
  );

  const handleAcceptVehicle = useCallback(
    async (relocationOrder: RelocationOrder) => {
      await toastMutationStatus({
        mutationFn: () => branchAcceptVehicleMutation(relocationOrder),
        successMessage: t("fit:VehicleAcceptedSuccessfully"),
        errorMessage: t("fit:ErrorAcceptingVehicle"),
      });
    },
    [branchAcceptVehicleMutation, t],
  );

  const handleCompleteOrder = useCallback(
    async (relocationOrder: RelocationOrder) => {
      await toastMutationStatus({
        mutationFn: () => branchCompleteMutation(relocationOrder),
        successMessage: t("fit:OrderCompletedSuccessfully"),
        errorMessage: t("fit:ErrorCompletingOrder"),
      });
    },
    [branchCompleteMutation, t],
  );

  const handleRejectVehicle = useCallback((relocationOrder: RelocationOrder) => {
    setSelectedRelocationOrder(relocationOrder);
    setRejectDialogIsOpen(true);
  }, []);

  const handleConfirmReject = useCallback(
    async (status: RelocationOrderBranchRejectStatus) => {
      if (!selectedRelocationOrder) return;

      await toastMutationStatus({
        mutationFn: () =>
          branchRejectVehicleMutation({
            relocationOrder: selectedRelocationOrder,
            body: { status },
          }),
        successMessage: t("fit:VehicleDeclinedSuccessfully"),
        errorMessage: t("fit:ErrorDecliningVehicle"),
      });
      setRejectDialogIsOpen(false);
    },
    [selectedRelocationOrder, branchRejectVehicleMutation, t],
  );

  const handleBlockVehicle = useCallback(async (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setBlockReason(VehicleBlockReason.Maintenance);
    setBlockDuration(1);
    setBlockDialogOpen(true);
  }, []);

  const handleCancelBlockVehicle = useCallback(() => {
    setBlockDialogOpen(false);
    setSelectedVehicle(null);
  }, []);

  const handleConfirmBlockVehicle = useCallback(async () => {
    if (!selectedVehicle || !user.uuid) return;

    const payload = createVehicleBlockPayload(selectedVehicle.uuid, user.uuid, blockReason, blockDuration);

    await toastMutationStatus({
      mutationFn: () => createVehicleBlockMutation(payload),
      successMessage: t("fit:VehicleSuccessfullyBlocked"),
      errorMessage: t("fit:ErrorBlockingVehicle"),
    });

    setBlockDialogOpen(false);
    setSelectedVehicle(null);
  }, [selectedVehicle, user.uuid, blockReason, blockDuration, createVehicleBlockMutation, t]);

  const handleUnblockVehicle = useCallback(
    async (vehicle: Vehicle) => {
      if (!vehicle.vehicleBlock?.uuid) return;

      await toastMutationStatus({
        mutationFn: () => deleteVehicleBlockMutation({ uuid: vehicle.vehicleBlock?.uuid }),
        successMessage: t("fit:VehicleSuccessfullyUnblocked"),
        errorMessage: t("fit:ErrorUnblockingVehicle"),
      });
    },
    [deleteVehicleBlockMutation, t],
  );

  const context = useMemo<RelocationOrderActionsContext>(
    () => ({
      t,
      handleApproveReadyForTransport,
      handleResolveTransportCompanyIssue,
      handleResolveBranchRejectedIssue,
      handleAcceptVehicle,
      handleRejectVehicle,
      handleBlockVehicle,
      handleUnblockVehicle,
      handleCompleteOrder,
    }),
    [
      t,
      handleApproveReadyForTransport,
      handleResolveTransportCompanyIssue,
      handleResolveBranchRejectedIssue,
      handleAcceptVehicle,
      handleRejectVehicle,
      handleBlockVehicle,
      handleUnblockVehicle,
      handleCompleteOrder,
    ],
  );

  const getActionsForOrder = useCallback(
    (relocationOrder: RelocationOrder): RowAction[] => {
      return buildActionsForOrder(relocationOrder, userBranchesUuids, context);
    },
    [userBranchesUuids, context],
  );

  const handleCancelRelocationOrder = useCallback(
    async (relocationOrder: RelocationOrder) => {
      await toastMutationStatus({
        mutationFn: () => cancelRelocationOrderMutation({ uuid: relocationOrder.uuid }),
        successMessage: t("fit:RelocationOrderCancelled"),
        errorMessage: t("fit:ErrorCancellingRelocationOrder"),
      });
    },
    [cancelRelocationOrderMutation, t],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<RelocationOrderRow>): RowAction[] => {
      const relocationOrder = row.original;
      const rowActions: RowAction[] = [...getActionsForOrder(relocationOrder)];

      rowActions.push({
        label: t("fit:ViewOrder"),
        icon: <EyeIcon className="h-4 w-4" />,
        onClick: () => navigate(`/fit/relocation-order/${relocationOrder.uuid}`),
      });

      if (canManage) {
        rowActions.push({
          label: t("fit:AdminUpdate"),
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => navigate(`/fit/relocation-order/${relocationOrder.uuid}/update`),
        });
      }

      if (canCancelRelocationOrder(ability, relocationOrder)) {
        rowActions.push({
          label: t("fit:CancelRelocationOrder"),
          icon: <BanIcon className="h-4 w-4" />,
          variant: RowActionVariant.Error,
          disabled: isCancellingRelocationOrder,
          needsConfirm: true,
          confirmLabels: {
            title: t("fit:CancelRelocationOrder"),
            text: t("fit:CancelRelocationOrderDescription"),
            confirmButton: t("fit:CancelRelocationOrder"),
            cancelButton: t("common:Back"),
          },
          onClick: () => handleCancelRelocationOrder(relocationOrder),
        });
      }

      return sortRelocationOrderActions(rowActions);
    },
    [getActionsForOrder, handleCancelRelocationOrder, isCancellingRelocationOrder, t, ability, canManage],
  );

  const dialogs = (
    <>
      <RejectVehicleDialog
        open={rejectDialogIsOpen}
        onOpenChange={setRejectDialogIsOpen}
        onConfirm={handleConfirmReject}
        vehicle={selectedRelocationOrder?.vehicle}
      />

      <ResolveIssueDialog
        open={resolveDialogIsOpen}
        onOpenChange={setResolveDialogIsOpen}
        onConfirm={handleConfirmResolveIssue}
        isTransportCompanyRejection={resolveMode === ResolveMode.TransportCompany}
      />

      <VehicleBlockDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        blockReason={blockReason}
        onBlockReasonChange={setBlockReason}
        blockDuration={blockDuration}
        onBlockDurationChange={setBlockDuration}
        onConfirm={handleConfirmBlockVehicle}
        onCancel={handleCancelBlockVehicle}
        isLoading={isBlockingVehicle}
      />
    </>
  );

  return {
    userBranchesUuids,
    isLoadingUserBranches,
    canManage,
    customRowActions,
    getActionsForOrder,
    dialogs,
  };
}
